package models

import kotlinx.serialization.Serializable

/**
 * Small, durable set of table moments used by the post-game recap and the live game stats. This
 * deliberately records outcomes rather than a turn-by-turn history: per-player tallies, one short
 * summary per round (rounds are capped at 20), and an 11-bucket dice histogram. That is enough to
 * chart the whole game without turning the game document into an ever-growing event log.
 *
 * The analytics fields ([playerStats], [rounds], [rollTotals]) were added after the highlight
 * moments. Older app builds drop them when they write the recap, so every reader must treat them
 * as best-effort (see GameAnalytics, which checks the round history against the real scores).
 */
@Serializable
data class GameRecapStats(
    val biggestPot: GameRecapMoment? = null,
    val biggestBank: GameRecapMoment? = null,
    val biggestBust: GameRecapMoment? = null,
    @Serializable(with = GameRecapCountsSerializer::class)
    val sevenCounts: List<GameRecapPlayerCount> = emptyList(),
    @Serializable(with = GameRecapPlayerStatsListSerializer::class)
    val playerStats: List<PlayerGameStats> = emptyList(),
    @Serializable(with = GameRecapRoundsListSerializer::class)
    val rounds: List<RoundRecap> = emptyList(),
    // How often each dice total came up: index 0 is a total of 2, index 10 a total of 12. Only
    // rolls with a known total count — a manually entered "Doubles" has none.
    @Serializable(with = GameRecapIntListSerializer::class)
    val rollTotals: List<Int> = emptyList(),
)

@Serializable
data class GameRecapMoment(
    val playerName: String = "",
    val points: Int = 0,
    val roundNum: Int = 0,
)

@Serializable
data class GameRecapPlayerCount(
    val playerName: String = "",
    val count: Int = 0,
)

@Serializable
data class GameRecapPlayerPoints(
    val playerName: String = "",
    val points: Int = 0,
)

/** One player's running tallies for the current game. Ints only: Firestore on Kotlin/JS rejects Longs. */
@Serializable
data class PlayerGameStats(
    val playerName: String = "",
    val rolls: Int = 0,
    val sevens: Int = 0,
    val doubles: Int = 0,
    // Rolls by this player that busted the round for everyone still riding.
    val bustsRolled: Int = 0,
    val banks: Int = 0,
    // Everything banked, including Heist Window bonuses.
    val pointsBanked: Int = 0,
    val bestBank: Int = 0,
    // Sum over banks of how many rolls the round had survived when they banked; divide by [banks]
    // for the player's average nerve.
    val rollsSurvivedAtBank: Int = 0,
    // Rounds that busted while this player was still riding, and the pot they had at stake.
    val timesBusted: Int = 0,
    val pointsLostToBusts: Int = 0,
)

/** What happened in one round. [scoreChanges] is each player's net score change for the round. */
@Serializable
data class RoundRecap(
    val roundNum: Int = 0,
    val rolls: Int = 0,
    val peakPot: Int = 0,
    val busted: Boolean = false,
    val bustedBy: String = "",
    // True once the round is over — a bust, or the last rider banked.
    val completed: Boolean = false,
    val modifier: RoundModifier = RoundModifier.NONE,
    @Serializable(with = GameRecapScoreChangesSerializer::class)
    val scoreChanges: List<GameRecapPlayerPoints> = emptyList(),
)

/** Records the pot peak, a bust (including Ginko/modifier busts), and the roller's dice tallies. */
fun GameRecapStats.afterRoll(
    previousRound: Round,
    updatedRound: Round,
    rollerName: String,
    rolledValue: Int,
): GameRecapStats {
    val busted = updatedRound.roundNum > previousRound.roundNum
    // On a bust the updated pot may be a modifier's seed for the *next* round. The pot that was
    // actually at risk is the previous one; a surviving roll owns the newly reached peak.
    val potAtMoment = if (busted) previousRound.currentPoints else updatedRound.currentPoints
    val roundAtMoment = previousRound.roundNum
    val potMoment = GameRecapMoment(
        playerName = rollerName,
        points = potAtMoment,
        roundNum = roundAtMoment,
    )
    val updatedSevenCounts = if (rolledValue == 7) {
        incrementPlayerCount(sevenCounts, rollerName)
    } else {
        sevenCounts
    }
    val rolledDoubles = isKnownDoubles(updatedRound.dieOne, updatedRound.dieTwo, rolledValue)

    var updatedPlayerStats = updatePlayerStats(playerStats, rollerName) {
        it.copy(
            rolls = it.rolls + 1,
            sevens = it.sevens + if (rolledValue == 7) 1 else 0,
            doubles = it.doubles + if (rolledDoubles) 1 else 0,
            bustsRolled = it.bustsRolled + if (busted) 1 else 0,
        )
    }
    if (busted) {
        previousRound.activeOrderedPlayerNames.forEach { rider ->
            updatedPlayerStats = updatePlayerStats(updatedPlayerStats, rider) {
                it.copy(
                    timesBusted = it.timesBusted + 1,
                    pointsLostToBusts = it.pointsLostToBusts + previousRound.currentPoints,
                )
            }
        }
    }

    val updatedRounds = updateRound(rounds, previousRound) { recap ->
        recap.copy(
            rolls = recap.rolls + 1,
            peakPot = if (busted) recap.peakPot else maxOf(recap.peakPot, updatedRound.currentPoints),
            busted = busted,
            bustedBy = if (busted) rollerName else recap.bustedBy,
            completed = recap.completed || busted,
        )
    }

    return copy(
        biggestPot = biggerMoment(biggestPot, potMoment),
        biggestBust = if (busted && previousRound.currentPoints > 0) {
            biggerMoment(biggestBust, potMoment)
        } else {
            biggestBust
        },
        sevenCounts = updatedSevenCounts,
        playerStats = updatedPlayerStats,
        rounds = updatedRounds,
        rollTotals = incrementRollTotal(rollTotals, rolledValue),
    )
}

/**
 * Records the largest pot successfully taken off the table by any one banker, plus each banker's
 * tallies. [heistBonuses] and [heistSkims] carry a Heist Window's point transfer (bankers gain,
 * riders lose) so the round's score changes add up to the real scoreboard. [roundEnded] is true
 * when this bank emptied the round.
 */
fun GameRecapStats.afterBank(
    round: Round,
    bankingPlayerNames: List<String>,
    heistBonuses: Map<String, Int> = emptyMap(),
    heistSkims: Map<String, Int> = emptyMap(),
    roundEnded: Boolean = false,
): GameRecapStats {
    if (bankingPlayerNames.isEmpty()) return this

    val firstBanker = bankingPlayerNames.first()
    val potMoment = GameRecapMoment(
        playerName = firstBanker,
        points = round.currentPoints,
        roundNum = round.roundNum,
    )
    val bankMoment = bankingPlayerNames
        .asSequence()
        .map { playerName -> potMoment.copy(playerName = playerName) }
        .fold(biggestBank, ::biggerMoment)

    val rollsSurvived = (round.currentRoll - 1).coerceAtLeast(0)
    var updatedPlayerStats = playerStats
    bankingPlayerNames.forEach { banker ->
        val banked = round.currentPoints + (heistBonuses[banker] ?: 0)
        updatedPlayerStats = updatePlayerStats(updatedPlayerStats, banker) {
            it.copy(
                banks = it.banks + 1,
                pointsBanked = it.pointsBanked + banked,
                bestBank = maxOf(it.bestBank, banked),
                rollsSurvivedAtBank = it.rollsSurvivedAtBank + rollsSurvived,
            )
        }
    }

    val updatedRounds = updateRound(rounds, round) { recap ->
        var changes = recap.scoreChanges
        bankingPlayerNames.forEach { banker ->
            changes = addPoints(changes, banker, round.currentPoints + (heistBonuses[banker] ?: 0))
        }
        heistSkims.forEach { (rider, skim) ->
            if (skim != 0) changes = addPoints(changes, rider, -skim)
        }
        recap.copy(
            peakPot = maxOf(recap.peakPot, round.currentPoints),
            scoreChanges = changes,
            completed = recap.completed || roundEnded,
        )
    }

    return copy(
        biggestPot = biggerMoment(biggestPot, potMoment),
        biggestBank = bankMoment,
        playerStats = updatedPlayerStats,
        rounds = updatedRounds,
    )
}

/** All players tied for the most sevens, in stable table order. */
fun GameRecapStats.sevenLeaders(): List<GameRecapPlayerCount> {
    val high = sevenCounts.maxOfOrNull { it.count } ?: return emptyList()
    return sevenCounts.filter { it.count == high && it.count > 0 }
}

/**
 * Splits a Heist Window's total skim across the bankers in banking order, handing the remainder
 * out one point at a time from the first banker. Shared by both data sources and the recap so the
 * scoreboard and the round history can never disagree.
 */
fun heistBonusesFor(bankingPlayerNames: List<String>, totalSkim: Int): Map<String, Int> {
    if (bankingPlayerNames.isEmpty() || totalSkim <= 0) return emptyMap()
    val base = totalSkim / bankingPlayerNames.size
    val remainder = totalSkim % bankingPlayerNames.size
    return bankingPlayerNames.withIndex().associate { (index, name) ->
        name to base + if (index < remainder) 1 else 0
    }
}

/** Dice totals the histogram can hold (2..12). */
val ROLL_TOTAL_RANGE: IntRange = 2..12

private fun isKnownDoubles(dieOne: Int, dieTwo: Int, rolledValue: Int): Boolean = when {
    dieOne != 0 && dieOne == dieTwo -> true
    // Manual entry: the "Doubles" key, or a total that can only be made with a pair.
    dieOne == 0 && dieTwo == 0 -> rolledValue == MANUAL_DOUBLES_VALUE || rolledValue == 2 || rolledValue == 12
    else -> false
}

private fun incrementRollTotal(totals: List<Int>, rolledValue: Int): List<Int> {
    if (rolledValue !in ROLL_TOTAL_RANGE) return totals
    val bucketCount = ROLL_TOTAL_RANGE.count()
    val padded = if (totals.size >= bucketCount) totals else totals + List(bucketCount - totals.size) { 0 }
    val index = rolledValue - ROLL_TOTAL_RANGE.first
    return padded.toMutableList().apply { this[index] = this[index] + 1 }
}

private inline fun updatePlayerStats(
    stats: List<PlayerGameStats>,
    playerName: String,
    transform: (PlayerGameStats) -> PlayerGameStats,
): List<PlayerGameStats> {
    if (playerName.isBlank()) return stats
    val index = stats.indexOfFirst { it.playerName == playerName }
    if (index < 0) return stats + transform(PlayerGameStats(playerName = playerName))
    return stats.toMutableList().apply { this[index] = transform(this[index]) }
}

private inline fun updateRound(
    rounds: List<RoundRecap>,
    round: Round,
    transform: (RoundRecap) -> RoundRecap,
): List<RoundRecap> {
    val index = rounds.indexOfFirst { it.roundNum == round.roundNum }
    if (index < 0) {
        val fresh = RoundRecap(
            roundNum = round.roundNum,
            peakPot = round.currentPoints,
            modifier = round.modifier,
        )
        return (rounds + transform(fresh)).sortedBy { it.roundNum }
    }
    return rounds.toMutableList().apply { this[index] = transform(this[index]) }
}

private fun addPoints(
    changes: List<GameRecapPlayerPoints>,
    playerName: String,
    points: Int,
): List<GameRecapPlayerPoints> {
    val index = changes.indexOfFirst { it.playerName == playerName }
    if (index < 0) return changes + GameRecapPlayerPoints(playerName = playerName, points = points)
    return changes.toMutableList().apply {
        this[index] = this[index].copy(points = this[index].points + points)
    }
}

private fun incrementPlayerCount(
    counts: List<GameRecapPlayerCount>,
    playerName: String,
): List<GameRecapPlayerCount> {
    val index = counts.indexOfFirst { it.playerName == playerName }
    if (index < 0) return counts + GameRecapPlayerCount(playerName = playerName, count = 1)
    return counts.toMutableList().apply {
        this[index] = this[index].copy(count = this[index].count + 1)
    }
}

private fun biggerMoment(
    current: GameRecapMoment?,
    candidate: GameRecapMoment,
): GameRecapMoment? = when {
    candidate.points <= 0 -> current
    current == null || candidate.points > current.points -> candidate
    else -> current
}

// The manual-entry grid's "Doubles" key (see FourByThreeBoxGrid).
private const val MANUAL_DOUBLES_VALUE = 13
